import { useCallback, useRef, useState } from "react";

import type { Gesture, Point } from "../types.ts";
import { getSteppedZoom, MAX_ZOOM, MIN_ZOOM, ZOOM_STEPS } from "./constants.ts";
import { ZoomContext, type ZoomUnaryIncrement } from "./ZoomContext";

const clampZoom = (zoom: number) => Math.min(Math.max(MIN_ZOOM, zoom), MAX_ZOOM);

const useZoomState = (initialValue: number): ZoomContext => {
  // A perspective saved before these steps existed can sit outside them.
  const [zoom, setZoom] = useState(() => clampZoom(initialValue));
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0, y: 0 });
  const zoomAtRest = useRef(clampZoom(initialValue));

  // The controls zoom about the centre of the view, only a wheel zoom has a cursor to zoom towards.
  const setCentredZoom = useCallback((newZoom: number) => {
    zoomAtRest.current = newZoom;
    setZoomOrigin({ x: 0, y: 0 });
    setZoom(newZoom);
  }, []);

  const onZoomStep = useCallback(
    (delta: ZoomUnaryIncrement) => setCentredZoom(getSteppedZoom(delta, zoom)),
    [setCentredZoom, zoom],
  );

  const onZoomChange = useCallback((newZoom: number) => setCentredZoom(clampZoom(newZoom)), [setCentredZoom]);

  const onZoomReset = useCallback(() => setCentredZoom(1), [setCentredZoom]);

  const onZoomWheel = useCallback((newZoom: number, origin: Point) => {
    const clampedZoom = clampZoom(newZoom);

    zoomAtRest.current = clampedZoom;
    setZoomOrigin(origin);
    setZoom(clampedZoom);
  }, []);

  const onZoomGesture = useCallback(
    (zoomGesture?: Gesture) => {
      if (zoomGesture) {
        setZoom(clampZoom(zoomAtRest.current * zoomGesture.scale));
        setZoomOrigin(zoomGesture.origin);
      } else {
        setZoomOrigin({ x: 0, y: 0 });
        zoomAtRest.current = zoom;
      }
    },
    [zoom],
  );

  return {
    zoom,
    zoomOrigin,
    zoomSteps: ZOOM_STEPS,
    onZoomStep,
    onZoomChange,
    onZoomWheel,
    onZoomReset,
    onZoomGesture,
  };
};

export const ZoomProvider = ({ initialValue, children }: { initialValue: number; children: React.ReactNode }) => {
  const zoomState = useZoomState(initialValue);
  return <ZoomContext.Provider value={zoomState}>{children}</ZoomContext.Provider>;
};
